import { useGetCompletedPonds, useGetCompletedPondsByFarms } from 'modules/pond/api/queries'
import { useFarms } from 'modules/farm/api/queries'
import { DestinationSelection } from 'modules/shrimpTransfer/api/types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Divider from '@mui/material/Divider'
import FormHelperText from '@mui/material/FormHelperText'
import Popover from '@mui/material/Popover'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import TransferFormRow from '../TransferFormRow'
import TransferIcon from '../TransferIcon'

type PickerView = 'ponds' | 'farms'

interface PondOption {
  id: string
  name: string
}

/** Ask the picker to re-open, optionally on a given farm. `id` must change each time. */
export interface PickerReopenRequest {
  id: number
  farmId?: string
}

interface DestinationPondPickerProps {
  value: DestinationSelection | null
  onChange: (selection: DestinationSelection) => void
  /** Farm opened by default - the farm of the source pond. */
  currentFarmId: string
  /** Source pond: never offered as a destination. */
  sourcePondId?: string
  speciesId?: number
  disabled?: boolean
  error?: boolean
  helperText?: string
  /** Re-opens the popover (e.g. after the chosen pond went stale), on that pond's farm. */
  reopenRequest?: PickerReopenRequest
  /** Grey label of the row ("move to pond"). */
  label: string
}

const BUTTON_ID = 'shrimp-transfer-destination'
const LABEL_ID = 'shrimp-transfer-destination-label'

const DestinationPondPicker = ({
  value,
  onChange,
  currentFarmId,
  sourcePondId,
  speciesId,
  disabled,
  error,
  helperText,
  reopenRequest,
  label,
}: DestinationPondPickerProps) => {
  const { t } = useTranslation('shrimpTransfer')
  const anchorRef = useRef<HTMLElement | null>(null)
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<PickerView>('ponds')
  const [browseFarmId, setBrowseFarmId] = useState(currentFarmId)

  const { data: farmData } = useFarms()
  const farms = farmData?.farms ?? []
  // Users without Farm read permission get no farm list: no names, nothing to switch to.
  const farmName = (id: string) => farms.find((farm) => farm.id === id)?.name ?? ''
  const canChangeFarm = farms.length > 1

  const params = { excludePondId: sourcePondId, speciesId }
  const {
    data: browseData,
    isLoading: isPondsLoading,
    isError: isPondsError,
    refetch: refetchPonds,
  } = useGetCompletedPonds({
    farmId: browseFarmId,
    ...params,
    enabled: open,
  })
  const farmQueries = useGetCompletedPondsByFarms(
    farms.map((farm) => farm.id),
    params,
    open && view === 'farms'
  )

  const openPicker = () => {
    setBrowseFarmId(value?.farmId || currentFarmId)
    setView('ponds')
    setOpen(true)
  }

  const lastReopenIdRef = useRef(reopenRequest?.id)
  useEffect(() => {
    if (reopenRequest && reopenRequest.id !== lastReopenIdRef.current) {
      lastReopenIdRef.current = reopenRequest.id
      setBrowseFarmId(reopenRequest.farmId || currentFarmId)
      setView('ponds')
      setOpen(true)
    }
  }, [reopenRequest, currentFarmId])

  const handleSelectPond = (pond: PondOption) => {
    onChange({
      pondId: pond.id,
      pondName: pond.name,
      farmId: browseFarmId,
      farmName: farmName(browseFarmId),
    })
    setOpen(false)
  }

  const handleSelectFarm = (farmId: string) => {
    setBrowseFarmId(farmId)
    setView('ponds')
  }

  const ponds = browseData?.ponds ?? []
  const browseFarmName = farmName(browseFarmId)
  const browseFarmLabel = `${
    browseFarmName
      ? t('transfer.farmLabel', { name: browseFarmName })
      : t('transfer.currentFarmFallback')
  }${browseFarmId === currentFarmId && browseFarmName ? ` ${t('transfer.thisFarm')}` : ''}`

  return (
    <Box>
      <TransferFormRow
        ref={anchorRef}
        icon={<TransferIcon name="location" size={2.5} />}
        label={label}
        labelId={LABEL_ID}
        onClick={openPicker}
        disabled={disabled}
        action={<ChevronRightIcon />}
        ariaProps={{
          id: BUTTON_ID,
          'aria-haspopup': 'listbox',
          'aria-expanded': open,
          'aria-invalid': !!error,
        }}
      >
        {value ? (
          <Box>
            <Typography fontWeight={700}>
              {t('transfer.pondLabel', { name: value.pondName })}
            </Typography>
            {value.farmName && (
              <Typography variant="body2" color="text.secondary">
                {`${t('transfer.farmLabel', { name: value.farmName })}${
                  value.farmId === currentFarmId ? ` ${t('transfer.thisFarm')}` : ''
                }`}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary">{t('transfer.destinationPlaceholder')}</Typography>
        )}
      </TransferFormRow>
      {helperText && (
        <FormHelperText error={error} sx={{ mx: 0 }}>
          {helperText}
        </FormHelperText>
      )}
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: anchorRef.current?.offsetWidth,
            maxHeight: (theme) => theme.spacing(45),
            mt: 0.5,
          },
        }}
      >
        {view === 'ponds' ? (
          <Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5 }}
            >
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('transfer.farmHeading')}
                </Typography>
                <Typography>{browseFarmLabel}</Typography>
              </Box>
              {canChangeFarm && (
                <ButtonBase
                  onClick={() => setView('farms')}
                  sx={{ color: 'primary.dark', fontWeight: 600 }}
                >
                  {t('transfer.changeFarm')}
                </ButtonBase>
              )}
            </Stack>
            <Divider />
            {isPondsLoading ? (
              <Box sx={{ px: 2, py: 1 }}>
                {[0, 1].map((idx) => (
                  <Skeleton
                    key={idx}
                    data-testid="pond-skeleton"
                    sx={(theme) => ({ height: theme.spacing(6) })}
                  />
                ))}
              </Box>
            ) : isPondsError ? (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography fontWeight={600}>{t('transfer.loadPondsFailed')}</Typography>
                <Button size="small" onClick={() => refetchPonds()} sx={{ mt: 1 }}>
                  {t('transfer.retry')}
                </Button>
              </Box>
            ) : ponds.length === 0 ? (
              <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                <Typography fontWeight={600}>{t('transfer.noEmptyPondTitle')}</Typography>
                {canChangeFarm && (
                  <Typography variant="body2" color="text.secondary">
                    {t('transfer.noEmptyPondHint')}
                  </Typography>
                )}
              </Box>
            ) : (
              <Box role="listbox" aria-label={t('transfer.destinationPlaceholder')}>
                {ponds.map((pond) => (
                  <ButtonBase
                    key={pond.id}
                    role="option"
                    aria-selected={value?.pondId === pond.id}
                    onClick={() => handleSelectPond(pond)}
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.75,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography fontWeight={600}>
                      {t('transfer.pondLabel', { name: pond.name })}
                    </Typography>
                    <Typography variant="body2" color="primary.dark">
                      {t('transfer.pondEmptyHint')}
                    </Typography>
                  </ButtonBase>
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box>
            <ButtonBase
              onClick={() => setView('ponds')}
              sx={{ width: '100%', justifyContent: 'flex-start', gap: 1, px: 2, py: 1.5 }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
              <Typography fontWeight={600}>{t('transfer.selectFarm')}</Typography>
            </ButtonBase>
            <Divider />
            <Box role="listbox" aria-label={t('transfer.selectFarm')}>
              {farms.map((farm, idx) => {
                const query = farmQueries[idx]
                const emptyCount = query?.data?.ponds.length
                return (
                  <ButtonBase
                    key={farm.id}
                    role="option"
                    aria-selected={farm.id === browseFarmId}
                    onClick={() => handleSelectFarm(farm.id)}
                    sx={{
                      width: '100%',
                      justifyContent: 'space-between',
                      px: 2,
                      py: 1.75,
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Typography fontWeight={600}>
                      {`${t('transfer.farmLabel', { name: farm.name })}${
                        farm.id === currentFarmId ? ` ${t('transfer.thisFarm')}` : ''
                      }`}
                    </Typography>
                    {query?.isLoading ? (
                      <Skeleton sx={(theme) => ({ width: theme.spacing(10) })} />
                    ) : query?.isError ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('transfer.loadFailedShort')}
                      </Typography>
                    ) : !emptyCount ? (
                      <Typography variant="body2" color="text.secondary">
                        {t('transfer.noEmptyPond')}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="primary.dark">
                        {t('transfer.farmPondSummary', {
                          total: farm.total_pond,
                          empty: emptyCount,
                        })}
                      </Typography>
                    )}
                  </ButtonBase>
                )
              })}
            </Box>
          </Box>
        )}
      </Popover>
    </Box>
  )
}

export default DestinationPondPicker
